import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight, Star } from 'lucide-react'

import { author } from '@/constants/site'
import { testimonials } from '@/constants/content'
import { SplitText } from '@/components/motion/split-text'
import { Reveal } from '@/components/motion/reveal'
import { RevealImage } from '@/components/motion/reveal-image'

const [lead, second] = testimonials

const Stars: FC = (): ReactNode => (
    <span className='flex gap-0.5' aria-hidden='true'>
        {[0, 1, 2, 3, 4].map((index) => (
            <Star
                key={index}
                className='size-3 fill-[color:var(--gold-ink)] text-[color:var(--gold-ink)]'
            />
        ))}
    </span>
)

export const Statement: FC = (): ReactNode => {
    return (
        <section id='intro' className='edge bg-background py-20 md:py-28' aria-labelledby='intro-heading'>
            <div className='mx-auto max-w-[110rem]'>
                {/* Byline left, the rating the headline answers to on the right. */}
                <Reveal className='flex flex-wrap items-start justify-between gap-8'>
                    <div className='flex items-center gap-3'>
                        <span className='relative size-9 overflow-hidden rounded-full'>
                            <Image src={author.portrait} alt='' fill sizes='36px' className='object-cover' />
                        </span>

                        <span className='text-[0.6875rem] leading-tight'>
                            <span className='block font-medium'>{author.name}</span>
                            <span className='block text-muted-foreground'>{author.role}</span>
                        </span>
                    </div>

                    <div className='max-w-xs'>
                        <div className='mb-2 flex items-center gap-1.5'>
                            <Stars />
                            <span className='text-[0.6875rem] font-medium'>{lead.rating} / 5</span>
                        </div>

                        <p className='text-[0.8125rem] leading-relaxed text-muted-foreground'>
                            {lead.quote}
                        </p>
                    </div>
                </Reveal>

                <div className='mt-14 md:mt-20'>
                    <span
                        aria-hidden='true'
                        className='script block text-[3.5rem] leading-[0.5] text-[color:var(--gold-ink)]'
                    >
                        &ldquo;
                    </span>

                    <h2 id='intro-heading' className='sr-only'>
                        About the studio
                    </h2>

                    <SplitText
                        as='p'
                        lines={[
                            'I don’t just take photos',
                            'I capture the raw emotions, the',
                            'subtle moments that make life beautiful'
                        ]}
                        className='display-xl mt-5 max-w-[38ch] md:max-w-[46ch]'
                        stagger={0.05}
                    />
                </div>

                {/* Three-column bento. Each column carries a different weight so
                    the row reads as a composition rather than a row of cards. */}
                <div className='mt-14 grid gap-4 md:mt-20 lg:grid-cols-3'>
                    <RevealImage
                        src='/images/cap-intro-portrait.jpg'
                        alt='Studio portrait of a seated subject against a plain backdrop'
                        className='aspect-[4/5] rounded-[var(--radius-lg)] lg:aspect-auto lg:min-h-[30rem]'
                        sizes='(max-width: 1024px) 100vw, 33vw'
                        parallax={10}
                    />

                    <div className='flex flex-col gap-4'>
                        <Reveal className='card-soft flex flex-1 flex-col justify-between p-7' delay={0.05}>
                            <p className='text-[0.9375rem] leading-relaxed text-foreground/80'>
                                Whether you are marking love, family, or a milestone that only
                                makes sense to you, I am here to photograph it with care, and to
                                hand you something you will still want to look at in twenty years.
                            </p>

                            <p className='label mt-8 text-muted-foreground'>
                                Edinburgh, and anywhere north of it
                            </p>
                        </Reveal>

                        <Reveal className='card-soft p-7' delay={0.12}>
                            <div className='mb-3 flex items-center gap-1.5'>
                                <Stars />
                                <span className='text-[0.6875rem] font-medium'>{second.rating} / 5</span>
                            </div>

                            <p className='text-[0.875rem] leading-relaxed text-foreground/80'>
                                {second.quote}
                            </p>

                            <div className='mt-5 flex items-center gap-2.5'>
                                <span className='relative size-7 overflow-hidden rounded-full'>
                                    <Image
                                        src={second.avatar}
                                        alt=''
                                        fill
                                        sizes='28px'
                                        className='object-cover'
                                    />
                                </span>

                                <span className='text-[0.6875rem] leading-tight'>
                                    <span className='block font-medium'>{second.name}</span>
                                    <span className='block text-muted-foreground'>{second.role}</span>
                                </span>
                            </div>
                        </Reveal>
                    </div>

                    <div className='flex flex-col gap-4'>
                        <RevealImage
                            src='/images/cap-family.jpg'
                            alt='Family sitting together outdoors during a commissioned session'
                            className='aspect-[5/4] rounded-[var(--radius-lg)]'
                            sizes='(max-width: 1024px) 100vw, 33vw'
                            delay={0.1}
                            parallax={8}
                        />

                        <Reveal className='card-soft flex flex-1 flex-col justify-between p-7' delay={0.18}>
                            <p className='display-md'>Ready to capture your moments?</p>

                            <Link
                                href='/contact'
                                className='pill mt-6 self-start bg-foreground text-background hover:bg-foreground/85'
                            >
                                Book a session
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </Link>
                        </Reveal>
                    </div>
                </div>

                <Reveal className='mt-10' delay={0.1}>
                    <Link
                        href='/portfolio'
                        className='pill border border-border hover:bg-foreground hover:text-background'
                    >
                        Check out my work
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </Link>
                </Reveal>
            </div>
        </section>
    )
}
